<template>
  <nav ref="menu" :class="classes">
    <Collapsible
      v-for="(navGroup, n) in navGroups"
      :key="n"
      class="collapsible"
      :title="navGroup.title"
      :icon="navGroup.icon"
      :is-open="n === 0 ? true : false"
    >
      <NavItem
        v-for="(item, index) in navGroup.items"
        :key="item.i18nName + index"
        :title="item.title || $t(item.i18nName)"
        :to="item.link"
        :path="item.path"
        expanded
        drawer
        @click.native="$emit('hide', $event)"
      />
    </Collapsible>

    <div
      v-if="$route.path !== '/shizen-issues' ? true : false"
      class="jira-issue-trigger"
    >
      <div class="jira-issue-trigger__sep" />
      <RouterLink
        :to="{ query: { ...$route.query, jiraIssuesReporter: true } }"
        class="generic-border generic-border--blue-border generic-border--text-btn"
      >
        {{ $t('message.JIRA_TRIGGER') }}
      </RouterLink>
      <RouterLink to="/shizen-issues" class="generic-border generic-border--blue-border generic-border--text-btn">
        {{ $t('message.JIRA_TRIGGER_BUGS') }}
      </RouterLink>
    </div>

    <div v-if="currentTeam && currentTeam.id" class="nav-drawer__version">
      <div>{{ $t('message.V') }}{{ systemVersion }}</div>
      <div>{{ $t('message.TEAM_ID', { id: (currentTeam.id + '-' + currentTeam.strId) }) }}</div>
      <div>{{ $t('message.TEAM_NAME', { name: decodeHTML(currentTeam.name) }) }}</div>
    </div>
  </nav>
</template>

<script>
/**
 * Expandable menu containing all available pages for this user.
 */

import $ from 'jquery';
// Import components
import Collapsible from '@/components/collapsible.vue';
import NavItem from '@/components/nav-item.vue';
// Mixins
import mixinMeetingPages from '@/mixins/meeting-pages.vue';
// Import Vuex utils
import { GET_MENU_CFG_PAGES } from '@/store/mutation-types';
// Import utils
import { NAV_LIST_CONFIG_DEFAULT } from '@/utils/navigation-constants';
import SPage from '@/utils/classes/SPage';
import { decodeHTML } from '@/utils/main';


export default {

  name: 'NavDrawer',

  components: {
    Collapsible,
    NavItem,
  },

  mixins: [mixinMeetingPages],

  props: {
    isMenuShown: Boolean,
  },

  computed: {
    classes() {
      return {
        'nav-drawer': true,
        'nav-drawer--visible': !!this.isMenuShown,
      };
    },
    systemVersion() {
      return process.env.VUE_APP_VERSION;
    },
    /** User may add some customized pages into the left menu */
    menuCfgPages() {
      return this.$store.state[GET_MENU_CFG_PAGES.stateKey] || [];
    },
    navGroupsConfig() {
      return [{
        title: this.$t('message.MAIN_MENU'),
        icon: 'dashboardMedium',
        items: NAV_LIST_CONFIG_DEFAULT.map((item) => {
          const page = SPage.fromId[`${item.pageId}`];
          const ct = this.currentTeam;
          const defaultHomePage = (ct && ct.defaultHomePage) || { pageId: SPage.DASHBOARD.id };
          page.link = page.id === SPage.CFG_LEAVE.id ? this.getPageLink(defaultHomePage) : page.link;
          return page;
        }),
      }];
    },
    navGroupsNormal() {
      if (this.currentTeam && !this.currentTeam.id) {
        return [];
      }
      const navGroups = [{
        title: this.$t('message.MAIN_MENU'),
        icon: 'generalMedium',
        items: [
          SPage.DASHBOARD,
          SPage.INDICATORS,
          SPage.ACTION_PLANS,
          SPage.PENDING,
          SPage.PROBLEMS,
          SPage.SCHEDULE,
          SPage.INFORMATIONS,
          SPage.STATS,
          SPage.ARCHIVES,
          SPage.SETTINGS,
          SPage.LOGOUT,
        ],
      }];
      if (this.menuCfgPages.length > 0) {
        // Team has custom pages
        navGroups.push({
          title: this.$t('message.CFG_PAGES'),
          icon: 'cfgPageMedium',
          items: this.menuCfgPages.map(p => (
            Object.assign({}, SPage.CFG_PAGE, { title: p.name, link: `${SPage.CFG_PAGE.link}?pageId=${p.id}` })
          )),
        });
      }
      if (this.currentTeam && this.currentTeam.hierarchyLevel > 0) {
        // This team may need to manage other teams
        // navGroups.push({
        //   title: this.$t('message.VITALITY_HUB'),
        //   items: [
        //     SPage.VITALITY_HUB,
        //   ],
        // });
        navGroups.push({
          title: this.$t('message.TEAMS_MANAGEMENT'),
          icon: 'managementMedium',
          items: [
            SPage.MGT_INDICATORS,
            SPage.MGT_ACTIONS,
            SPage.MGT_PROBLEMS,
            SPage.MGT_STATS,
          ],
        });
      }
      if (this.currentTeam && this.currentTeam.accessGW === 1) {
        // The company has access to GW features
        navGroups.push({
          title: this.$t('message.GW_MENU'),
          icon: 'hardhatExtraBold',
          items: [
            SPage.GW_COMPLETED_LIST,
            SPage.GW_TEMPLATES,
            SPage.GW_STATS,
            SPage.GW_CHECKLISTS,
            SPage.GW_INDICATORS,
          ],
        });
      }
      return navGroups;
    },
    navGroups() {
      if (this.$route.meta.loginPage) {
        return [];
      }
      if (this.$route.meta.requireConfig) {
        return this.navGroupsConfig;
      }
      return this.navGroupsNormal;
    },
  },

  mounted() {
    // Hide menu on click outside
    $(document).click((event) => {
      if (!$(event.target).closest(this.$refs.menu).length) {
        if (this.isMenuShown) {
          this.$emit('hide', event);
        }
      }
    });
  },

  methods: {
    decodeHTML,
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.nav-drawer {
  width: $nav-width;
  position: fixed;
  top: $nav-bar-drawer-top-position;
  left: -$nav-width;
  height: calc(100% - #{$nav-bar-drawer-top-position});
  z-index: 10019;
  background: $color-shizen-blue;
  transition: transform 0.3s ease;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 0.4);
    }

    &:active {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  &--visible {
    transform: translateX($nav-width);
  }

  &__group h2 {
    font-weight: 600;
    color: #999;
    margin: 0.5em 0 0.3em 0;
    padding-top: 5px;
  }

  &__version {
    color: #ffffff;
    margin: 30px 20px;
    font-style: italic;

    div {
      margin-top: 5px;
    }
  }

  .jira-issue-trigger {
    color: #fff;
    padding: 1rem;

    &__sep {
      height: 1px;
      background: #fff;
      opacity: 0.4;
      width: 100%;
      margin: 4rem 0;
    }

    .generic-border {
      color: #fff;
      font-weight: 600;
      display: inline-flex;
      margin-top: .5rem;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .nav-drawer {
    top: 0px;
    height: 100%;
  }
}
</style>
